import {
  runtimeConfig as appRuntimeConfig,
  type LogLevel,
} from "../config/runtimeConfig";

interface LoggingConfig {
  enabled: boolean;
  level: LogLevel;
}

const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

let runtimeConfig: LoggingConfig = {
  enabled: true,
  level: appRuntimeConfig.logLevel || "debug",
};

class Logger {
  private context: string;

  constructor(context = "") {
    this.context = context;
  }

  private _shouldLog = (level: LogLevel): boolean => {
    const currentLogLevel = LOG_LEVELS[runtimeConfig.level] ?? LOG_LEVELS.debug;
    return runtimeConfig.enabled && LOG_LEVELS[level] >= currentLogLevel;
  };

  private _formatMessage = (
    level: LogLevel,
    message: unknown,
    ...args: unknown[]
  ): unknown[] => {
    const prefix = this.context ? `[${this.context}][${level}]` : `[${level}]`;
    return [prefix, message, ...args].filter(Boolean);
  };

  debug = (message: unknown, ...args: unknown[]): void => {
    if (this._shouldLog("debug")) {
      console.log(...this._formatMessage("debug", message, ...args));
    }
  };

  info = (message: unknown, ...args: unknown[]): void => {
    if (this._shouldLog("info")) {
      console.info(...this._formatMessage("info", message, ...args));
    }
  };

  warn = (message: unknown, ...args: unknown[]): void => {
    if (this._shouldLog("warn")) {
      console.warn(...this._formatMessage("warn", message, ...args));
    }
  };

  error = (message: unknown, ...args: unknown[]): void => {
    if (this._shouldLog("error")) {
      console.error(...this._formatMessage("error", message, ...args));
    }
  };

  object = (label: string, obj: unknown): void => {
    if (this._shouldLog("debug")) {
      console.log(`[${this.context}] ${label}:`, obj);
    }
  };
}

export function createLogger(context: string): Logger {
  return new Logger(context);
}

export function setLoggingConfig(config: Partial<LoggingConfig> = {}): void {
  if (typeof config.enabled === "boolean") {
    runtimeConfig.enabled = config.enabled;
  }
  if (config.level && LOG_LEVELS[config.level] !== undefined) {
    runtimeConfig.level = config.level;
  }
}
